import React, { useEffect, useState } from 'react';
import { Api } from '@blink/components';
import { ethers, formatUnits, parseUnits } from 'ethers';
import { ALLOWANCE_STATUS } from '@blink/components/src/types';
import { ReactComponent as CautionIcon } from '@blink/assets/src/icons/warningNotFilled.svg';
import { ReactComponent as SuccessIcon } from '@blink/assets/src/icons/checkCircleNotFilled.svg';

import { AllowanceStep } from './AllowanceStep/AllowanceStep';

import { usePrevious } from '@/shared/hooks/usePrevious';
import IERCAbi from '@/shared/abi/IERCAbi.json';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import { Status } from '@/widgets/Status';
import { AssetWithIcon } from '@/shared/ui';

type AllowanceType = {
    status: ALLOWANCE_STATUS;
    requiredAmount: string;
    asset: string;
    assetAddress: string;
    changeStatus: (status: ALLOWANCE_STATUS) => void;
    signer: ethers.JsonRpcSigner;
    lastItem?: boolean;
};

const RESET_ALLOWANCE_ERROR_MESSAGE = 'execution reverted';

export const assetsToResetAllowance = new Set().add('USDT');

const statusClassMap: Record<string, string> = {
    processing: 'border border-border-accent rounded-overall',
    waiting: 'border border-border-opaque rounded-overall',
    error: 'border border-border-negative rounded-overall',
    success: 'border border-border-accent rounded-overall',
    warning: 'border border-border-warning rounded-overall',
};

export const Allowance = ({
    asset,
    status,
    requiredAmount,
    assetAddress,
    changeStatus,
    signer,
    lastItem,
}: AllowanceType) => {
    const statusClass = statusClassMap[status] || '';

    const [errorAllowance, setErrorAllowance] = useState(
        assetsToResetAllowance.has(asset) || false,
    );
    const [contractException, setContractException] = useState(false);
    const [currentAllowance, setCurrentAllowance] = useState('');

    const [currentAllowanceLoading, setCurrentAllowanceLoading] = useState(true);
    const [resetAllowanceLoading, setResetAllowanceLoading] = useState(false);
    const [tryAgain, setTryAgain] = useState<boolean>(true);
    const isWaitingStatus = status === ALLOWANCE_STATUS.WAITING;
    const prevStatus = usePrevious(status);
    const isPrevStatusProcessing = prevStatus === ALLOWANCE_STATUS.PROCESSING;
    const isStatusChangedToSkip =
        isPrevStatusProcessing &&
        (status === ALLOWANCE_STATUS.WARNING || status === ALLOWANCE_STATUS.ERROR);

    useEffect(() => {
        (async () => {
            if (!status || isWaitingStatus || isStatusChangedToSkip) {
                return;
            }
            const token = getSafeContract(assetAddress, IERCAbi, signer);

            const userAllowanceBalance = await token.allowance(
                signer.address,
                Api.APP_DISPATCHER_ADDRESS,
            );
            const decimal = await token.decimals();
            const amount = parseUnits(requiredAmount.toString(), decimal);

            const readableUserAllowance = formatUnits(userAllowanceBalance, decimal);
            setCurrentAllowance(readableUserAllowance);
            const isResetRequired =
                Number(readableUserAllowance) !== 0 &&
                Number(readableUserAllowance) < Number(requiredAmount);
            const isAllowanceNeedToReset = errorAllowance && isResetRequired;

            /** Check is need to reset allowance to 0 */
            if (isAllowanceNeedToReset) {
                try {
                    setResetAllowanceLoading(true);
                    const approveToken = await token.approve(Api.APP_DISPATCHER_ADDRESS, 0);
                    await approveToken.wait();
                    setResetAllowanceLoading(false);
                } catch (e: any) {
                    console.warn(e);
                    if (e?.message?.toLowerCase().includes(RESET_ALLOWANCE_ERROR_MESSAGE)) {
                        setErrorAllowance(true);
                        setTryAgainHandler();
                        return;
                    }

                    if (e?.code === 'ACTION_REJECTED') {
                        setErrorAllowance(true);
                        changeStatus(ALLOWANCE_STATUS.WARNING);
                        console.warn('Allowance warning! User Reject to reset allowance!');
                        return;
                    }
                }
            }

            /** Check is user allowance more than required to make transaction */
            if (Number(readableUserAllowance) >= Number(requiredAmount)) {
                setCurrentAllowanceLoading(false);
                changeStatus(ALLOWANCE_STATUS.SUCCESS);
                return;
            }

            /** Make a new allowance for a user */
            try {
                const approveToken = await token.approve(Api.APP_DISPATCHER_ADDRESS, amount);
                await approveToken.wait();
                const newUserAllowance = await token.allowance(
                    signer.address,
                    Api.APP_DISPATCHER_ADDRESS,
                );
                const readableNewUserAllowance = formatUnits(newUserAllowance, decimal);
                if (Number(readableNewUserAllowance) < Number(requiredAmount)) {
                    setTryAgainHandler();
                    setCurrentAllowanceLoading(true);
                    return;
                }
                setCurrentAllowanceLoading(false);
                changeStatus(ALLOWANCE_STATUS.SUCCESS);
            } catch (e: any) {
                console.warn(e);
                if (e?.message?.toLowerCase().includes(RESET_ALLOWANCE_ERROR_MESSAGE)) {
                    setErrorAllowance(true);
                    return;
                }
                if (e?.code === 'ACTION_REJECTED') {
                    changeStatus(ALLOWANCE_STATUS.WARNING);
                    console.warn('Allowance warning! Trying to reset allowance to 0!');
                    return;
                }
                setContractException(true);
                changeStatus(ALLOWANCE_STATUS.ERROR);
            }
        })();
    }, [errorAllowance, tryAgain, status]);

    const resetYourBalance = () => {
        const newStatus = !resetAllowanceLoading ? ALLOWANCE_STATUS.SUCCESS : status;
        return errorAllowance &&
            !contractException &&
            Number(currentAllowance) !== 0 &&
            Number(currentAllowance) < Number(requiredAmount) ? (
            <AllowanceStep
                amount='0'
                title='Reset your current allowance'
                isLoading={resetAllowanceLoading}
                status={newStatus}
            />
        ) : null;
    };

    const setTryAgainHandler = () => {
        changeStatus(ALLOWANCE_STATUS.PROCESSING);
        setTryAgain((prevState) => !prevState);
    };

    const warningMessage = () => {
        return status === ALLOWANCE_STATUS.WARNING ? (
            <div
                className='flex w-full min-h-[55px] mt-3 text-sm bg-background-yellow50 text-content-yellow800 [&>svg]:mr-3 [&>svg]:w-5 [&>svg]:h-5 
                [&_path]:fill-content-yellow800 rounded-overall'
            >
                <div className='flex items-center justify-between w-full p-4'>
                    <div className='flex gap-3'>
                        <CautionIcon />
                        <span>Transaction was rejected by user.</span>
                    </div>
                    <div
                        className='mr-3 text-content-primary underline cursor-pointer text-sm'
                        onClick={setTryAgainHandler}
                    >
                        Try Again
                    </div>
                </div>
            </div>
        ) : null;
    };

    const successMessage = () => {
        return status === ALLOWANCE_STATUS.SUCCESS && lastItem ? (
            <div className='flex mt-3 p-4 gap-3 bg-background-lightPositive text-content-green800 rounded-overall w-fit'>
                <SuccessIcon />
                <div className='flex items-center justify-between w-full text-sm leading-[16px]'>
                    <span>
                        Great job! Your previous allowance has been reset and a new allowance has
                        been set! Now gear up, the next one is about to kick off!
                    </span>
                </div>
            </div>
        ) : null;
    };

    const getCurrentAllowance = (): string => {
        return currentAllowance === '' ? '' : currentAllowance;
    };

    return (
        <div className='w-full'>
            <h1 className='text-content-primary text-[14px] mb-3'>Token Approval for {asset}</h1>
            <main className={`p-5 text-content-primary ${statusClass}`}>
                <header className='flex items-center justify-between pb-5'>
                    <div className='[&_img]:!h-[28px] [&_img]:!w-[28px]'>
                        <p className='font-medium text-content-fourth mb-4 text-[14px]'>Token</p>
                        <AssetWithIcon assetName={asset} />
                    </div>
                    <div className='flex flex-col items-end'>
                        <p className='font-medium text-content-fourth mb-4 text-[14px]'>
                            Token Status
                        </p>
                        <Status status={status} />
                    </div>
                </header>
                {status === ALLOWANCE_STATUS.WAITING ? (
                    <div className='flex justify-between items-center [&>span:first-child]:font-medium [&>span:last-child]:font-semibold'>
                        <span className='text-[14px]'>Allowance</span>
                        <span className='text-[14px]'>0</span>
                    </div>
                ) : (
                    <>
                        <AllowanceStep
                            amount={getCurrentAllowance()}
                            title='Your current allowance'
                            isLoading={setCurrentAllowance === null}
                            status={status}
                            border
                        />
                        {resetYourBalance()}
                        <AllowanceStep
                            amount={requiredAmount}
                            title='Required allowance'
                            isLoading={currentAllowanceLoading}
                            status={status}
                        />
                    </>
                )}
            </main>
            {warningMessage()}
            {successMessage()}
        </div>
    );
};
